import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, AlertTriangle } from 'lucide-react';
import { alertResumeService } from '../services/alertResume.service';
import { messageHandler } from '../services/message-handler.service';
import type { AlertResume, AlertResumeFilters } from '../types/alertResume';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Pagination from '../components/ui/Pagination';
import AlertResumeList from '../components/alert-resumes/AlertResumeList';
import AlertResumeForm from '../components/alert-resumes/AlertResumeForm';
import AlertResumeFiltersComponent from '../components/alert-resumes/AlertResumeFilters';

const DEFAULT_LIMIT = 20;

type ModalType = 'create' | 'edit' | '';

interface ModalState {
  type: ModalType;
}

function AlertResumes() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingResume, setEditingResume] = useState<AlertResume | null>(null);
  const [filters, setFilters] = useState<AlertResumeFilters>({ page: 1, limit: DEFAULT_LIMIT });
  const queryClient = useQueryClient();

  const { data: response, isLoading } = useQuery({
    queryKey: ['alert-resumes', filters],
    queryFn: () => alertResumeService.getAll(filters),
    keepPreviousData: true,
  });

  const resumes = response?.data || [];
  const pagination = response?.pagination;

  const createMutation = useMutation({
    mutationFn: (data: Partial<AlertResume>) => alertResumeService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-resumes'] });
      messageHandler.success('Resumo de alerta criado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<AlertResume> }) =>
      alertResumeService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-resumes'] });
      messageHandler.success('Resumo de alerta atualizado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => alertResumeService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-resumes'] });
      messageHandler.success('Resumo de alerta excluído com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (id: number) => alertResumeService.markAsResolved(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-resumes'] });
      messageHandler.success('Alerta marcado como resolvido!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      resume: formData.get('resume') as string,
      resolved: formData.get('resolved') === 'on',
      alert_type: (formData.get('alert_type') as string) || 'manual',
    };

    if (editingResume) {
      updateMutation.mutate({ id: editingResume.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (resume: AlertResume) => {
    messageHandler.confirm(
      'Excluir Alerta',
      'Tem certeza que deseja excluir este resumo de alerta?',
      () => deleteMutation.mutate(resume.id)
    );
  };

  const handleResolve = (resume: AlertResume) => {
    messageHandler.confirm(
      'Resolver Alerta',
      'Tem certeza que deseja marcar este alerta como resolvido?',
      () => resolveMutation.mutate(resume.id)
    );
  };

  const handleFilterChange = (newFilters: AlertResumeFilters) => {
    setFilters({ ...newFilters, page: 1, limit: DEFAULT_LIMIT });
  };

  const handleResetFilters = () => {
    setFilters({ page: 1, limit: DEFAULT_LIMIT });
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingResume(null);
  };

  const hasFilters =
    filters.resolved !== undefined ||
    filters.user_account_id !== undefined ||
    filters.alert_type !== undefined;

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Resumos de Alertas"
          description="Gerencie os resumos de alertas do sistema."
          icon={AlertTriangle}
          actions={
            <Button
              onClick={() => {
                setEditingResume(null);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Alerta
            </Button>
          }
        />

        <AlertResumeFiltersComponent
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
        />

        <AlertResumeList
          resumes={resumes}
          onEdit={resume => {
            setEditingResume(resume);
            setModal({ type: 'edit' });
          }}
          onDelete={handleDelete}
          onResolve={handleResolve}
          isLoading={isLoading}
          isFiltered={hasFilters}
        />

        {pagination && pagination.last_page > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={pagination.current_page}
              totalPages={pagination.last_page}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingResume ? 'Editar Resumo de Alerta' : 'Novo Resumo de Alerta'}
        >
          <AlertResumeForm
            resume={editingResume}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={closeModal}
          />
        </Modal>
      </div>
    </div>
  );
}

export default AlertResumes;
